// ToneProfileDisplay.tsx — FE_SPEC §3.X.3.11 (NEW · M2 gap close).
//
// Renders Composer's `tone_profile` (4 axes · [0,10] each) as a row of
// progress bars. Pure display; no event handlers. 4 substates per
// PR-FE-SPEC-02:
//   default: 4 axis bars + numeric value
//   loading: profile===undefined → skeleton
//   empty:   all 4 values===0 → "Tone neutral" copy
//   error:   any value out of [0,10] → clamp + console.warn
//
// Source: docs/contracts/m2/dto.m2.schema.json#/$defs/ToneProfile.

import { useTranslation } from 'react-i18next';

export interface ToneProfile {
  politeness: number;
  elegance: number;
  trustworthy: number;
  comforting: number;
}

interface ToneProfileDisplayProps {
  /** When undefined, renders a skeleton (loading substate). */
  tone_profile?: ToneProfile | null;
  compact?: boolean;
}

const AXIS_IDS = ['politeness', 'elegance', 'trustworthy', 'comforting'] as const;

function clamp(n: number, min = 0, max = 10): number {
  if (n < min || n > max) {
    // eslint-disable-next-line no-console
    console.warn('tone_profile_out_of_range', { value: n, clampedTo: Math.max(min, Math.min(max, n)) });
  }
  return Math.max(min, Math.min(max, n));
}

export default function ToneProfileDisplay({
  tone_profile,
  compact = false,
}: ToneProfileDisplayProps) {
  const { t } = useTranslation();

  // loading substate
  if (tone_profile === undefined) {
    return (
      <div
        data-testid="tone-profile-display"
        data-state="loading"
        className="animate-pulse h-16 bg-paper-2 rounded"
        aria-busy="true"
        aria-label={t('tone.title', { defaultValue: 'Council tone' })}
      />
    );
  }

  // empty substate (also covers null)
  if (
    tone_profile === null ||
    (tone_profile.politeness === 0 &&
      tone_profile.elegance === 0 &&
      tone_profile.trustworthy === 0 &&
      tone_profile.comforting === 0)
  ) {
    return (
      <div
        data-testid="tone-profile-display"
        data-state="empty"
        className="text-xs text-ink-3 font-mono"
      >
        {t('tone.neutral', { defaultValue: 'Tone neutral' })}
      </div>
    );
  }

  const values = AXIS_IDS.map((id) => ({
    id,
    v: clamp(tone_profile[id]),
  }));

  return (
    <section
      data-testid="tone-profile-display"
      data-state="default"
      aria-label={t('tone.title', { defaultValue: 'Council tone' })}
      className={compact ? 'tone-compact text-xs' : 'tone-default text-sm'}
    >
      <h4 className="sr-only">{t('tone.title', { defaultValue: 'Council tone' })}</h4>
      <ul role="list" className="flex flex-col gap-1">
        {values.map((a) => (
          <li
            key={a.id}
            className="flex items-center gap-2"
            data-axis={a.id}
            data-value={a.v}
          >
            <span className="text-ink-2 w-20 shrink-0">
              {t(`tone.${a.id}`, { defaultValue: a.id })}
            </span>
            <progress
              max={10}
              value={a.v}
              aria-label={`${t(`tone.${a.id}`, { defaultValue: a.id })} ${a.v}/10`}
              className="flex-1 h-1.5"
            />
            <span aria-hidden className="font-mono text-ink-3 w-10 text-right">
              {a.v}/10
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
